import { useState } from 'react';
import { Building2, Plus, Pencil, Trash2, Star, Users, UserPlus, X, MapPin, Phone, Mail } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useLocations, Location } from '@/hooks/use-locations';
import { useUsers, UserProfile } from '@/hooks/use-users';
import { useAuth } from '@/hooks/use-auth';

interface LocationFormData {
  name: string;
  address: string;
  phone: string;
  email: string;
}

const emptyForm: LocationFormData = { name: '', address: '', phone: '', email: '' };

export const LocationManagement = () => {
  const { companyId } = useAuth();
  const { locations, loading, createLocation, updateLocation, deleteLocation, setDefaultLocation, fetchLocations } = useLocations();
  const { users, updateUserLocation } = useUsers();

  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingLocation, setEditingLocation] = useState<Location | null>(null);
  const [deleteConfirmLocation, setDeleteConfirmLocation] = useState<Location | null>(null);
  const [assignUserLocationId, setAssignUserLocationId] = useState<string | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [formData, setFormData] = useState<LocationFormData>(emptyForm);
  const [saving, setSaving] = useState(false);

  const getUsersForLocation = (locationId: string): UserProfile[] => {
    return users.filter(u => u.location_id === locationId);
  };

  const getUnassignedUsers = (): UserProfile[] => {
    const locationIds = locations.map(l => l.id);
    return users.filter(u => !u.location_id || !locationIds.includes(u.location_id));
  };

  const getUsersFromOtherLocations = (currentLocationId: string): UserProfile[] => {
    return users.filter(u => u.location_id !== currentLocationId);
  };

  const handleCreate = async () => {
    if (!formData.name.trim()) return;
    setSaving(true);
    await createLocation({
      name: formData.name.trim(),
      address: formData.address.trim() || null,
      phone: formData.phone.trim() || null,
      email: formData.email.trim() || null,
      company_id: companyId!,
    });
    setSaving(false);
    setShowCreateDialog(false);
    setFormData(emptyForm);
  };

  const handleEdit = async () => {
    if (!editingLocation || !formData.name.trim()) return;
    setSaving(true);
    await updateLocation(editingLocation.id, {
      name: formData.name.trim(),
      address: formData.address.trim() || null,
      phone: formData.phone.trim() || null,
      email: formData.email.trim() || null,
    });
    setSaving(false);
    setEditingLocation(null);
    setFormData(emptyForm);
  };

  const handleDelete = async () => {
    if (!deleteConfirmLocation) return;
    await deleteLocation(deleteConfirmLocation.id);
    setDeleteConfirmLocation(null);
  };

  const handleToggleActive = async (location: Location) => {
    await updateLocation(location.id, { is_active: !location.is_active });
  };

  const handleSetDefault = async (location: Location) => {
    await setDefaultLocation(location.id);
  };

  const handleAssignUser = async () => {
    if (!assignUserLocationId || !selectedUserId) return;
    setSaving(true);
    const success = await updateUserLocation(selectedUserId, assignUserLocationId);
    if (success) {
      setSelectedUserId('');
    }
    setSaving(false);
  };

  const handleRemoveUser = async (userId: string) => {
    await updateUserLocation(userId, null);
  };

  const openEditDialog = (location: Location) => {
    setFormData({
      name: location.name,
      address: location.address || '',
      phone: location.phone || '',
      email: location.email || '',
    });
    setEditingLocation(location);
  };

  const getInitials = (name: string | null) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
  };

  const getRoleBadge = (role: string) => {
    const roleMap: Record<string, { label: string; variant: 'default' | 'secondary' | 'outline' }> = {
      admin: { label: 'Yönetici', variant: 'default' },
      manager: { label: 'Müdür', variant: 'secondary' },
      sales: { label: 'Satış', variant: 'outline' },
      cashier: { label: 'Kasiyer', variant: 'outline' },
    };
    const r = roleMap[role] || { label: role, variant: 'outline' as const };
    return <Badge variant={r.variant}>{r.label}</Badge>;
  };

  const assignableUsers = assignUserLocationId
    ? [...getUnassignedUsers(), ...getUsersFromOtherLocations(assignUserLocationId)]
        .filter((u, i, arr) => arr.findIndex(x => x.id === u.id) === i)
        .filter(u => u.location_id !== assignUserLocationId)
    : [];
  const targetLocation = assignUserLocationId
    ? locations.find((loc) => loc.id === assignUserLocationId) || null
    : null;

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5" />
            Lokasyonlar
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-muted-foreground text-sm">Yükleniyor...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5" />
            Lokasyonlar
          </CardTitle>
          <Button size="sm" onClick={() => { setFormData(emptyForm); setShowCreateDialog(true); }}>
            <Plus className="w-4 h-4 mr-1" />
            Yeni Şube
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          {locations.length === 0 ? (
            <div className="text-center text-muted-foreground py-8">
              Henüz şube eklenmemiş. İlk şubenizi oluşturun.
            </div>
          ) : (
            locations.map((location) => {
              const assignedUsers = getUsersForLocation(location.id);
              return (
                <div
                  key={location.id}
                  className={`rounded-xl p-4 space-y-4 border transition-all ${
                    location.is_active
                      ? 'border-primary/20 bg-gradient-to-br from-primary/5 via-background to-accent/5 shadow-sm'
                      : 'border-border bg-muted/30 opacity-70'
                  }`}
                >
                  {/* Location header */}
                  <div className="flex items-start justify-between">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <div className="h-8 w-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                          <Building2 className="w-4 h-4" />
                        </div>
                        <h3 className="font-semibold text-base">{location.name}</h3>
                        {location.is_default && (
                          <Badge variant="secondary" className="text-xs bg-amber-500/15 text-amber-700 border-amber-500/30">
                            <Star className="w-3 h-3 mr-1" />
                            Varsayılan
                          </Badge>
                        )}
                        {!location.is_active && (
                          <Badge variant="destructive" className="text-xs">Pasif</Badge>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-3 text-sm text-muted-foreground">
                        {location.address && (
                          <span className="flex items-center gap-1 rounded-md bg-background/80 px-2 py-1 border">
                            <MapPin className="w-3 h-3" />
                            {location.address}
                          </span>
                        )}
                        {location.phone && (
                          <span className="flex items-center gap-1 rounded-md bg-background/80 px-2 py-1 border">
                            <Phone className="w-3 h-3" />
                            {location.phone}
                          </span>
                        )}
                        {location.email && (
                          <span className="flex items-center gap-1 rounded-md bg-background/80 px-2 py-1 border">
                            <Mail className="w-3 h-3" />
                            {location.email}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      {!location.is_default && location.is_active && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          title="Varsayılan yap"
                          onClick={() => handleSetDefault(location)}
                        >
                          <Star className="w-4 h-4" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        title="Düzenle"
                        onClick={() => openEditDialog(location)}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <div className="flex items-center gap-1" title={location.is_active ? 'Pasife al' : 'Aktif et'}>
                        <Switch
                          checked={location.is_active ?? true}
                          onCheckedChange={() => handleToggleActive(location)}
                        />
                      </div>
                      {!location.is_default && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive hover:text-destructive"
                          title="Sil"
                          onClick={() => setDeleteConfirmLocation(location)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Assigned Users */}
                  <div className="border-t pt-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium flex items-center gap-1">
                        <Users className="w-4 h-4" />
                        Atanmış Kullanıcılar ({assignedUsers.length})
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 text-xs"
                        onClick={() => { setAssignUserLocationId(location.id); setSelectedUserId(''); }}
                      >
                        <UserPlus className="w-3 h-3 mr-1" />
                        Kullanıcı Ata
                      </Button>
                    </div>
                    {assignedUsers.length === 0 ? (
                      <p className="text-xs text-muted-foreground">Bu şubeye atanmış kullanıcı yok</p>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        {assignedUsers.map((user) => (
                          <div
                            key={user.id}
                            className="flex items-center gap-2 bg-background/90 border rounded-full pl-1 pr-2 py-1 shadow-sm"
                          >
                            <Avatar className="h-6 w-6">
                              <AvatarFallback className="text-xs bg-accent/20 text-accent">
                                {getInitials(user.full_name)}
                              </AvatarFallback>
                            </Avatar>
                            <span className="text-sm">{user.full_name || user.email}</span>
                            {getRoleBadge(user.role)}
                            <button
                              className="ml-1 text-muted-foreground hover:text-destructive transition-colors"
                              title="Şubeden çıkar"
                              onClick={() => handleRemoveUser(user.id)}
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </CardContent>
      </Card>

      {/* Create Location Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Yeni Şube Oluştur</DialogTitle>
            <DialogDescription>Yeni bir şube ekleyin ve detaylarını girin.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="loc-name">Şube Adı *</Label>
              <Input
                id="loc-name"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Merkez Şube"
              />
            </div>
            <div>
              <Label htmlFor="loc-address">Adres</Label>
              <Input
                id="loc-address"
                value={formData.address}
                onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                placeholder="Adres giriniz"
              />
            </div>
            <div>
              <Label htmlFor="loc-phone">Telefon</Label>
              <Input
                id="loc-phone"
                value={formData.phone}
                onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                placeholder="0212 xxx xx xx"
              />
            </div>
            <div>
              <Label htmlFor="loc-email">E-posta</Label>
              <Input
                id="loc-email"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                placeholder="sube@firma.com"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>İptal</Button>
            <Button onClick={handleCreate} disabled={!formData.name.trim() || saving}>
              {saving ? 'Oluşturuluyor...' : 'Oluştur'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Location Dialog */}
      <Dialog open={!!editingLocation} onOpenChange={(open) => { if (!open) setEditingLocation(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Şube Düzenle</DialogTitle>
            <DialogDescription>Şube bilgilerini güncelleyin.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="edit-name">Şube Adı *</Label>
              <Input
                id="edit-name"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              />
            </div>
            <div>
              <Label htmlFor="edit-address">Adres</Label>
              <Input
                id="edit-address"
                value={formData.address}
                onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
              />
            </div>
            <div>
              <Label htmlFor="edit-phone">Telefon</Label>
              <Input
                id="edit-phone"
                value={formData.phone}
                onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
              />
            </div>
            <div>
              <Label htmlFor="edit-email">E-posta</Label>
              <Input
                id="edit-email"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingLocation(null)}>İptal</Button>
            <Button onClick={handleEdit} disabled={!formData.name.trim() || saving}>
              {saving ? 'Kaydediliyor...' : 'Kaydet'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign User Dialog */}
      <Dialog open={!!assignUserLocationId} onOpenChange={(open) => { if (!open) { setAssignUserLocationId(null); setSelectedUserId(''); } }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-primary" />
              Kullanıcı Ata
            </DialogTitle>
            <DialogDescription>
              Bu şubeye bir kullanıcı atayın. Atanan kullanıcı sadece bu şubenin verilerini görecektir.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="rounded-lg border bg-muted/30 p-3">
              <div className="text-xs text-muted-foreground">Hedef Şube</div>
              <div className="font-medium">{targetLocation?.name || 'Bilinmeyen Şube'}</div>
              <div className="text-xs text-muted-foreground mt-1">
                Uygun kullanıcı sayısı: {assignableUsers.length}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Kullanıcı Seçin</Label>
              <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                <SelectTrigger>
                  <SelectValue placeholder="Kullanıcı seçin..." />
                </SelectTrigger>
                <SelectContent>
                  {assignableUsers.length === 0 ? (
                    <div className="p-2 text-sm text-muted-foreground text-center">
                      Atanabilecek kullanıcı yok
                    </div>
                  ) : (
                    assignableUsers.map((user) => (
                      <SelectItem key={user.id} value={user.id}>
                        <div className="flex items-center justify-between w-full min-w-[260px] gap-3">
                          <div className="flex items-center gap-2">
                            <Avatar className="h-6 w-6">
                              <AvatarFallback className="text-xs bg-primary/15 text-primary">
                                {getInitials(user.full_name)}
                              </AvatarFallback>
                            </Avatar>
                            <div className="flex flex-col">
                              <span className="text-sm leading-tight">{user.full_name || user.email}</span>
                              <span className="text-xs text-muted-foreground leading-tight">{user.email}</span>
                            </div>
                          </div>
                          <Badge variant="outline" className="text-xs">
                            {user.location_id ? 'Başka şubede' : 'Atanmamış'}
                          </Badge>
                        </div>
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setAssignUserLocationId(null); setSelectedUserId(''); }}>
              İptal
            </Button>
            <Button onClick={handleAssignUser} disabled={!selectedUserId || saving}>
              {saving ? 'Atanıyor...' : 'Ata'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteConfirmLocation} onOpenChange={(open) => { if (!open) setDeleteConfirmLocation(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Şubeyi Sil</AlertDialogTitle>
            <AlertDialogDescription>
              "{deleteConfirmLocation?.name}" şubesini silmek istediğinize emin misiniz?
              Bu işlem geri alınamaz. Şubede ürün veya satış varsa silme işlemi engellenecektir.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>İptal</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Sil
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
